// Port "Primitive" — Geometrie-Builder aus Jans Renderer.
//
// HERKUNFT
//
// pro-editor/src/storyforger/Renderer/primitiveGeometry.ts — die Geometrie-
// Bauteile (PRIMITIVE_DEFAULTS + buildPrimitiveGeometry + die sechs Custom-
// Builder). Faithful uebernommen.
//
// NICHT uebernommen: PRIMITIVE_PARAM_SPECS + PRIMITIVES (Editor-UI-Metadaten,
// braucht der Renderer nicht) und text3d/number3d (bereits im effects/text3d-
// Modul; werden hier nicht gebaut, sondern im Renderer async aus einer Typeface).
//
// ADAPTIONEN (three r139 -> unser 0.183.2):
//  - mergeBufferGeometries -> mergeGeometries (in 0.183 umbenannt).
//  - RoundedBoxGeometry + mergeGeometries aus dem THREE-Barrel.
//
// WICHTIG: reine Geometrie, kein Zustand. buildPrimitiveGeometry(kind, params)
// gibt eine THREE.BufferGeometry zurueck. magnifier/pin liefern Geometrie mit
// Material-Gruppen (Multi-Material) — der Renderer verdrahtet aktuell nur die
// Ein-Material-Primitive; magnifier/pin/chess bleiben dem naechsten Schritt.
import THREE, { RoundedBoxGeometry, mergeGeometries } from "../utils/THREE";
import type { PrimitiveKind, PrimitiveParams } from "@scrolly/post-types";

export const PRIMITIVE_DEFAULTS: Record<PrimitiveKind, PrimitiveParams> = {
    // grid-aligned defaults: the helper grid is 1024px, so a fresh cube/plane is
    // exactly one grid cell wide (duplicate + shift position by 0.5 = 1024 units
    // lands the copy perfectly in the next cell)
    box: { width: 1024, height: 1024, depth: 1024, subdivisions: 1 },
    sphere: { radius: 1024, widthSegments: 48, heightSegments: 32 },
    cylinder: { radius: 280, height: 700, radialSegments: 48 },
    cone: { radius: 350, height: 700, radialSegments: 48 },
    pyramid: { radius: 420, height: 700 },
    torus: { radius: 320, tube: 110, radialSegments: 24, tubularSegments: 64 },
    tube: { radius: 280, height: 700, radialSegments: 48 },
    plane: { width: 1024, height: 1024, subdivisions: 1 },
    text3d: {
        text: "Text",
        size: 300,
        depth: 60,
        bevel: 6,
        curveSegments: 6,
        font: "droidSans",
        letterSpacing: 0,
    },
    number3d: {
        start: 0,
        end: 100,
        decimals: 0,
        prefix: "",
        suffix: "",
        thousandsSep: "",
        size: 300,
        depth: 60,
        bevel: 6,
        curveSegments: 6,
        font: "droidSans",
        letterSpacing: 0,
    },
    progressBar: { width: 1400, height: 140, depth: 80 },
    // a solid, extruded ring band (not a thin torus tube). `tube` = band width.
    progressRing: { radius: 400, tube: 130, depth: 80, curveSegments: 64 },
    // radius = lens radius, tube = metal-rim thickness, depth = magnification
    // (drives the glass material's optical thickness), height = handle length
    magnifier: {
        radius: 500,
        tube: 35,
        depth: 770,
        height: 0,
        frameFinish: "black",
    },
    roundedBox: { width: 600, height: 600, depth: 600, radius: 80, subdivisions: 4 },
    capsule: { radius: 220, height: 600, radialSegments: 20 },
    icosahedron: { radius: 350, detail: 0 },
    octahedron: { radius: 380, detail: 0 },
    tetrahedron: { radius: 420, detail: 0 },
    dodecahedron: { radius: 360, detail: 0 },
    ring: { radius: 380, innerRadius: 210, radialSegments: 48, depth: 120, bevel: 0 },
    // radius = outer (tip) radius, innerRadius = valley radius
    star: { points: 5, radius: 420, innerRadius: 180, depth: 140, bevel: 12 },
    torusKnot: {
        radius: 280,
        tube: 90,
        tubularSegments: 128,
        radialSegments: 16,
        p: 2,
        q: 3,
    },
    // radius = coil radius, tube = tube thickness, turns = windings, height = total
    helix: { radius: 300, tube: 50, turns: 4, height: 900, radialSegments: 12 },
    // radius = head/thickness size, height = total height (tip → top)
    pin: {
        radius: 120,
        height: 520,
        pinStyle: "map",
        pinColor: { r: 230, g: 60, b: 50, a: 1 },
    },
    chess: {
        pgn: "",
        comments: "",
        squareSize: 400,
        reflection: 0.15,
        boardLight: { r: 235, g: 235, b: 210, a: 1 },
        boardDark: { r: 120, g: 150, b: 100, a: 1 },
        boardHighlight: { r: 247, g: 201, b: 69, a: 1 },
        whitePieceColor: { r: 240, g: 240, b: 235, a: 1 },
        blackPieceColor: { r: 40, g: 40, b: 45, a: 1 },
        showCoordinates: true,
        coordColor: { r: 60, g: 60, b: 60, a: 1 },
    },
};

export function buildProgressRingGeometry(p: PrimitiveParams = {}, progress = 1): THREE.BufferGeometry {
    const outer = p.radius ?? 400;
    const band = p.tube ?? 130;
    const inner = Math.max(1, outer - band);
    const depth = p.depth ?? 80;
    const seg = Math.max(8, Math.round(p.curveSegments ?? 64));
    const ratio = Math.min(Math.max(progress, 0), 1);
    const a0 = Math.PI / 2; // start at top
    const a1 = a0 + Math.max(0.0001, ratio * Math.PI * 2); // fill counter-clockwise

    const shape = new THREE.Shape();
    if (ratio >= 0.9999) {
        // full ring = outer circle with an inner hole
        shape.absarc(0, 0, outer, 0, Math.PI * 2, false);
        const hole = new THREE.Path();
        hole.absarc(0, 0, inner, 0, Math.PI * 2, true);
        shape.holes.push(hole);
    } else {
        // partial band: outer arc forward, radial in, inner arc back, radial out
        shape.moveTo(outer * Math.cos(a0), outer * Math.sin(a0));
        shape.absarc(0, 0, outer, a0, a1, false);
        shape.lineTo(inner * Math.cos(a1), inner * Math.sin(a1));
        shape.absarc(0, 0, inner, a1, a0, true);
        shape.lineTo(outer * Math.cos(a0), outer * Math.sin(a0));
    }
    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: false,
        curveSegments: seg,
    });
    // centre only the depth — keep the ring anchored on the circle origin so it
    // doesn't wander as the arc fills
    geometry.translate(0, 0, -depth / 2);
    return geometry;
}

/**
 * FORTSCHRITTSRING OHNE NEUAUFBAU (Jans Fund am Welcome-Scrolly, 07./08.09.2026,
 * gemessen: 24 Geometrie-Uploads je 500 ms Scroll, Bildzeiten 22-50 ms).
 *
 * `buildProgressRingGeometry` extrudiert bei jeder Winkelaenderung eine neue
 * Form. Hier entsteht der VOLLE Ring genau einmal als Sweep aus 360 Ein-Grad-
 * Segmenten, deren Dreiecke im Index in Winkelreihenfolge liegen. Pro Bild
 * aendert sich dann nur noch `drawRange` (s. progressRingDrawCount) — kein
 * Neuaufbau, kein Upload.
 *
 * Aufbau des Index: [Startkappe (radiale Flaeche bei 90 Grad)] dann je Segment
 * [Aussenwand, Innenwand, Vorderseite, Rueckseite, Endkappe]. Die Endkappe
 * jedes Segments liegt im Inneren des Ringkoerpers, sobald das naechste
 * Segment gezeichnet wird — unsichtbar, aber sie schliesst den Koerper am
 * jeweils letzten gezeichneten Segment sauber ab (kein Blick ins Innere).
 * Fuellrichtung wie bisher: Start oben, gegen den Uhrzeigersinn.
 */
export const PROGRESS_RING_SEGMENTS = 360
const RING_INDICES_PER_QUAD = 6
const RING_QUADS_PER_SEGMENT = 5
export const PROGRESS_RING_INDICES_PER_SEGMENT = RING_QUADS_PER_SEGMENT * RING_INDICES_PER_QUAD

export function buildProgressRingSweepGeometry(p: PrimitiveParams = {}): THREE.BufferGeometry {
    const outer = p.radius ?? 400;
    const band = p.tube ?? 130;
    const inner = Math.max(1, outer - band);
    const depth = p.depth ?? 80;
    const half = depth / 2;
    const N = PROGRESS_RING_SEGMENTS;
    const a0 = Math.PI / 2;

    const positions: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    // Ein Viereck aus vier Punkten (gegen den Uhrzeigersinn von aussen gesehen)
    // mit flacher Normale — wie die Flaechen einer ExtrudeGeometry ohne Fase.
    const quad = (
        ax: number, ay: number, az: number,
        bx: number, by: number, bz: number,
        cx: number, cy: number, cz: number,
        dx: number, dy: number, dz: number,
        nx: number, ny: number, nz: number,
    ) => {
        const base = positions.length / 3;
        positions.push(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz);
        for (let i = 0; i < 4; i++) normals.push(nx, ny, nz);
        uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
        indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    };
    // Radiale Kappe am Winkel `a` (Flaeche von innen nach aussen, ueber die Tiefe)
    const cap = (a: number, dir: number) => {
        const c = Math.cos(a), s = Math.sin(a);
        // Normale zeigt tangential: dir=-1 bei der Startkappe (zurueck),
        // dir=+1 bei der Endkappe (in Fuellrichtung)
        const nx = -s * dir, ny = c * dir;
        const ix = inner * c, iy = inner * s, ox = outer * c, oy = outer * s;
        if (dir > 0) quad(ix, iy, -half, ox, oy, -half, ox, oy, half, ix, iy, half, nx, ny, 0);
        else quad(ix, iy, half, ox, oy, half, ox, oy, -half, ix, iy, -half, nx, ny, 0);
    };

    cap(a0, -1);
    for (let i = 0; i < N; i++) {
        const t0 = a0 + (i / N) * Math.PI * 2;
        const t1 = a0 + ((i + 1) / N) * Math.PI * 2;
        const c0 = Math.cos(t0), s0 = Math.sin(t0), c1 = Math.cos(t1), s1 = Math.sin(t1);
        const ox0 = outer * c0, oy0 = outer * s0, ox1 = outer * c1, oy1 = outer * s1;
        const ix0 = inner * c0, iy0 = inner * s0, ix1 = inner * c1, iy1 = inner * s1;
        const mc = Math.cos((t0 + t1) / 2), ms = Math.sin((t0 + t1) / 2);
        // Aussenwand (Normale nach aussen)
        quad(ox0, oy0, -half, ox1, oy1, -half, ox1, oy1, half, ox0, oy0, half, mc, ms, 0);
        // Innenwand (Normale nach innen)
        quad(ix1, iy1, -half, ix0, iy0, -half, ix0, iy0, half, ix1, iy1, half, -mc, -ms, 0);
        // Vorderseite (+z)
        quad(ix0, iy0, half, ox0, oy0, half, ox1, oy1, half, ix1, iy1, half, 0, 0, 1);
        // Rueckseite (-z)
        quad(ox0, oy0, -half, ix0, iy0, -half, ix1, iy1, -half, ox1, oy1, -half, 0, 0, -1);
        // Endkappe dieses Segments
        cap(t1, 1);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.userData.__ringSweep = true;
    geometry.setDrawRange(0, progressRingDrawCount(1));
    return geometry;
}

/** Index-Anzahl fuer `drawRange` bei Fuellstand 0..1 (Startkappe + volle Grad-Segmente). */
export function progressRingDrawCount(progress: number): number {
    const ratio = Math.min(Math.max(progress, 0), 1);
    const segments = Math.round(ratio * PROGRESS_RING_SEGMENTS);
    return RING_INDICES_PER_QUAD + segments * PROGRESS_RING_INDICES_PER_SEGMENT;
}

// An extruded N-pointed star: alternating outer (tip) and inner (valley)
// vertices around the origin, extruded by `depth` with an optional bevel.
export function buildStarGeometry(p: PrimitiveParams = {}): THREE.BufferGeometry {
    const points = Math.max(3, Math.round(p.points ?? 5));
    const outer = Math.max(1, p.radius ?? 420);
    const inner = Math.max(1, Math.min(p.innerRadius ?? outer * 0.45, outer - 1));
    const depth = Math.max(0, p.depth ?? 140);
    const bevel = Math.max(0, p.bevel ?? 12);

    const shape = new THREE.Shape();
    const step = Math.PI / points; // half the angle between two tips
    for (let i = 0; i < points * 2; i++) {
        const r = i % 2 === 0 ? outer : inner;
        // start with a tip pointing UP (+Y); -PI/2 would point it down
        const a = Math.PI / 2 + i * step;
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r;
        if (i === 0) shape.moveTo(x, y);
        else shape.lineTo(x, y);
    }
    shape.closePath();

    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: bevel > 0,
        bevelThickness: bevel,
        bevelSize: bevel,
        bevelSegments: 2,
        curveSegments: 1,
    });
    geometry.center();
    return geometry;
}

// A ring band. Flat (THREE.RingGeometry) when depth is 0 — same as before — or a
// solid extruded band (outer circle with an inner hole) with an optional bevel
// when depth > 0, so the ring becomes a real 3D object.
export function buildRingGeometry(p: PrimitiveParams = {}): THREE.BufferGeometry {
    const outer = Math.max(1, p.radius ?? 380);
    const inner = Math.max(0, Math.min(p.innerRadius ?? 210, outer - 1));
    const seg = Math.max(3, Math.round(p.radialSegments ?? 48));
    const depth = Math.max(0, p.depth ?? 120);

    if (depth <= 0) {
        return new THREE.RingGeometry(inner, outer, seg);
    }

    const shape = new THREE.Shape();
    shape.absarc(0, 0, outer, 0, Math.PI * 2, false);
    if (inner > 0) {
        const hole = new THREE.Path();
        hole.absarc(0, 0, inner, 0, Math.PI * 2, true);
        shape.holes.push(hole);
    }
    // keep the bevel from overlapping the band width or meeting across the depth
    const band = outer - inner;
    const bevel = Math.max(0, Math.min(p.bevel ?? 0, depth / 2 - 0.5, band / 2 - 0.5));
    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: bevel > 0,
        bevelThickness: bevel,
        bevelSize: bevel,
        bevelSegments: 2,
        curveSegments: seg,
    });
    geometry.center();
    return geometry;
}

// A magnifying glass merged into ONE geometry with three material groups, in
// this order: 0 = glass lens, 1 = metal rim, 2 = handle.
export function buildMagnifierGeometry(p: PrimitiveParams = {}): THREE.BufferGeometry {
    const r = p.radius ?? 360; // lens radius
    const rim = p.tube ?? 70; // metal-rim thickness (torus tube)
    const handleLen = p.height ?? 760; // handle length (0 = no handle)

    const lens = new THREE.SphereGeometry(r, 64, 48);
    lens.scale(1, 1, 0.18);

    const rimGeo = new THREE.TorusGeometry(r + rim * 0.35, rim, 28, 96);

    const parts: THREE.BufferGeometry[] = [lens, rimGeo];

    if (handleLen > 0) {
        const handle = new THREE.CylinderGeometry(rim * 0.85, rim * 0.7, handleLen, 32);
        handle.translate(0, -handleLen / 2, 0); // top end at the origin, extends -Y
        handle.rotateZ(Math.PI / 4); // -Y → down-right (45°)
        const reach = r + rim * 0.1;
        handle.translate(reach * Math.SQRT1_2, -reach * Math.SQRT1_2, 0);
        parts.push(handle);
    }

    const merged = mergeGeometries(parts, true);
    return merged ?? lens;
}

// A helix / coil: a tube swept along a vertical spiral curve.
export function buildHelixGeometry(p: PrimitiveParams = {}): THREE.BufferGeometry {
    const coil = p.radius ?? 300;
    const tube = p.tube ?? 50;
    const turns = Math.max(0.25, p.turns ?? 4);
    const height = p.height ?? 900;
    const radialSegments = Math.max(3, Math.round(p.radialSegments ?? 12));
    // segments along the path scale with the turns so the coil stays smooth
    const tubularSegments = Math.min(2000, Math.max(64, Math.round(turns * 48)));

    // Adaption r139->0.183: Curve-Konstruktor ist jetzt protected (Subclass-
    // gedacht) — Cast, um Jans direkte Instanziierung beizubehalten.
    const path = new (THREE.Curve as unknown as { new (): THREE.Curve<THREE.Vector3> })();
    path.getPoint = (t: number, target = new THREE.Vector3()) => {
        const a = 2 * Math.PI * turns * t;
        return target.set(coil * Math.cos(a), height * (t - 0.5), coil * Math.sin(a));
    };
    const tubeGeo = new THREE.TubeGeometry(path, tubularSegments, tube, radialSegments, false);

    // cap both open ends so the tube reads as solid
    const makeCap = (t: number, outwardSign: number): THREE.BufferGeometry => {
        const pos = path.getPoint(t);
        const outward = path.getTangent(t).normalize().multiplyScalar(outwardSign);
        const cap = new THREE.CircleGeometry(tube, radialSegments);
        cap.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), outward));
        cap.translate(pos.x, pos.y, pos.z);
        return cap;
    };

    const merged = mergeGeometries([
        tubeGeo,
        makeCap(0, -1), // start: outward = against the path direction
        makeCap(1, 1), // end: outward = along the path direction
    ]);
    return merged ?? tubeGeo;
}

// A location pin / marker in one of four styles. Built as ONE geometry with two
// material groups (0 = coloured body, 1 = accent). Tip at origin, body points +Y.
export function buildPinGeometry(p: PrimitiveParams = {}): THREE.BufferGeometry {
    const r = p.radius ?? 120; // head / thickness size
    const h = p.height ?? 520; // total height (tip → top)
    const style = p.pinStyle ?? "map";
    const body: THREE.BufferGeometry[] = []; // group 0 (colour)
    const accent: THREE.BufferGeometry[] = []; // group 1 (white / metal)

    // a cone with its apex (tip) at the origin, pointing DOWN, base at +height
    const tipCone = (radius: number, coneH: number, seg = 32) => {
        const c = new THREE.ConeGeometry(radius, coneH, seg);
        c.rotateX(Math.PI); // apex points down
        c.translate(0, coneH / 2, 0); // apex at y=0, base at y=coneH
        return c;
    };

    if (style === "flat") {
        const R = r;
        const cy = Math.max(R * 1.7, h - R); // circle centre height (keeps d > R)
        const phi = Math.acos(Math.min(0.96, R / cy)); // tangent half-angle
        const aR = -Math.PI / 2 + phi;
        const aL = -Math.PI / 2 - phi;
        const shape = new THREE.Shape();
        shape.moveTo(0, 0); // tip
        shape.lineTo(R * Math.cos(aR), cy + R * Math.sin(aR)); // up the right side
        shape.absarc(0, cy, R, aR, aL + Math.PI * 2, false); // over the top
        shape.lineTo(0, 0); // back down the left side to the tip
        const hole = new THREE.Path();
        hole.absarc(0, cy, R * 0.45, 0, Math.PI * 2, true);
        shape.holes.push(hole);
        const depth = R * 0.5;
        const geo = new THREE.ExtrudeGeometry(shape, {
            depth,
            bevelEnabled: true,
            bevelThickness: R * 0.08,
            bevelSize: R * 0.08,
            bevelSegments: 3,
            curveSegments: 48,
        });
        geo.translate(0, 0, -depth / 2); // centre the thickness on z=0
        body.push(geo);
    } else if (style === "map") {
        const headR = r;
        const coneH = Math.max(headR, h - headR * 1.1);
        body.push(tipCone(headR * 0.92, coneH));
        const head = new THREE.SphereGeometry(headR, 36, 28);
        head.translate(0, coneH + headR * 0.5, 0);
        body.push(head);
        const dot = new THREE.SphereGeometry(headR * 0.42, 24, 18);
        dot.translate(0, coneH + headR * 0.5, headR * 0.72);
        accent.push(dot);
    } else if (style === "needle") {
        const ballR = r;
        const needleH = Math.max(ballR, h - ballR);
        const needle = new THREE.CylinderGeometry(r * 0.16, 0, needleH, 16);
        needle.translate(0, needleH / 2, 0); // point at origin
        accent.push(needle);
        const ball = new THREE.SphereGeometry(ballR, 32, 24);
        ball.translate(0, needleH + ballR * 0.85, 0);
        body.push(ball);
    } else if (style === "pushpin") {
        const headR = r;
        const pinH = h * 0.55;
        accent.push(tipCone(r * 0.22, pinH, 20));
        const headH = h * 0.16;
        const head = new THREE.CylinderGeometry(headR, headR * 0.8, headH, 32);
        head.translate(0, pinH + headH / 2, 0);
        body.push(head);
        const dome = new THREE.SphereGeometry(headR, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2);
        dome.translate(0, pinH + headH, 0);
        body.push(dome);
    } else {
        const ballR = r;
        const poleR = r * 0.16;
        const poleH = Math.max(ballR, h - ballR);
        const pole = new THREE.CylinderGeometry(poleR, poleR, poleH, 16);
        pole.translate(0, poleH / 2, 0);
        accent.push(pole);
        const foot = new THREE.CylinderGeometry(r * 0.7, r * 0.8, h * 0.05, 24);
        foot.translate(0, h * 0.025, 0);
        accent.push(foot);
        const ball = new THREE.SphereGeometry(ballR, 32, 24);
        ball.translate(0, poleH + ballR * 0.9, 0);
        body.push(ball);
    }

    const bodyGeo = mergeGeometries(body, false);
    if (accent.length === 0) {
        const geo = bodyGeo ?? new THREE.SphereGeometry(r);
        if (geo.groups.length === 0) geo.addGroup(0, Infinity, 0);
        return geo;
    }
    const accentGeo = mergeGeometries(accent, false);
    if (!bodyGeo || !accentGeo) return bodyGeo ?? accentGeo ?? new THREE.SphereGeometry(r);
    // two groups → [bodyColour, accent] material array (built in the renderer)
    return mergeGeometries([bodyGeo, accentGeo], true) ?? bodyGeo;
}

// Build a Three.js geometry for a primitive, merging the caller's params over
// the kind's defaults so partial/empty params still produce a valid shape.
export function buildPrimitiveGeometry(kind: PrimitiveKind, params: PrimitiveParams = {}): THREE.BufferGeometry {
    const p = { ...PRIMITIVE_DEFAULTS[kind], ...params };
    switch (kind) {
        case "box":
            return new THREE.BoxGeometry(p.width, p.height, p.depth, p.subdivisions, p.subdivisions, p.subdivisions);
        case "sphere":
            return new THREE.SphereGeometry(p.radius, p.widthSegments, p.heightSegments);
        case "cylinder":
            return new THREE.CylinderGeometry(p.radius, p.radius, p.height, p.radialSegments);
        case "cone":
            return new THREE.ConeGeometry(p.radius, p.height, p.radialSegments);
        case "pyramid":
            // a cone with 4 radial segments is a square-based pyramid
            return new THREE.ConeGeometry(p.radius, p.height, 4);
        case "torus":
            return new THREE.TorusGeometry(p.radius, p.tube, p.radialSegments, p.tubularSegments);
        case "tube":
            // an open-ended cylinder reads as a hollow tube
            return new THREE.CylinderGeometry(p.radius, p.radius, p.height, p.radialSegments, 1, true);
        case "plane":
            return new THREE.PlaneGeometry(p.width, p.height, p.subdivisions, p.subdivisions);
        case "progressBar": {
            // a full-length bar with its left edge on the origin (the "length 0"
            // pivot); the renderer rebuilds it shorter per frame from that point
            const bar = new THREE.BoxGeometry(p.width, p.height, p.depth);
            bar.translate((p.width ?? 1400) / 2, 0, 0);
            return bar;
        }
        case "progressRing":
            // voller Ring als Sweep — der Renderer stellt pro Bild nur drawRange
            // um (kein Neuaufbau mehr, s. buildProgressRingSweepGeometry)
            return buildProgressRingSweepGeometry(p);
        case "magnifier":
            return buildMagnifierGeometry(p);
        case "roundedBox":
            return new RoundedBoxGeometry(
                p.width,
                p.height,
                p.depth,
                Math.max(1, Math.round(p.subdivisions ?? 4)),
                p.radius
            ) as unknown as THREE.BufferGeometry;
        case "capsule":
            // length is the straight middle section (caps add `radius` on each end)
            return new THREE.CapsuleGeometry(
                p.radius,
                p.height,
                Math.max(4, Math.round((p.radialSegments ?? 20) / 2)),
                p.radialSegments
            );
        case "icosahedron":
            // detail MUST be a non-negative integer (a fractional value crashes the
            // subdivision) — round it defensively
            return new THREE.IcosahedronGeometry(p.radius, Math.max(0, Math.round(p.detail ?? 0)));
        case "octahedron":
            return new THREE.OctahedronGeometry(p.radius, Math.max(0, Math.round(p.detail ?? 0)));
        case "tetrahedron":
            return new THREE.TetrahedronGeometry(p.radius, Math.max(0, Math.round(p.detail ?? 0)));
        case "dodecahedron":
            return new THREE.DodecahedronGeometry(p.radius, Math.max(0, Math.round(p.detail ?? 0)));
        case "ring":
            return buildRingGeometry(p);
        case "star":
            return buildStarGeometry(p);
        case "torusKnot":
            return new THREE.TorusKnotGeometry(
                p.radius,
                p.tube,
                p.tubularSegments,
                p.radialSegments,
                Math.max(1, Math.round(p.p ?? 2)),
                Math.max(1, Math.round(p.q ?? 3))
            );
        case "helix":
            return buildHelixGeometry(p);
        case "pin":
            return buildPinGeometry(p);
        default:
            return new THREE.BoxGeometry(p.width, p.height, p.depth);
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TODOAnyP = any;

// Gebackenes Material-Array fuer den magnifier, gepaart mit den Geometrie-Gruppen
// in der Reihenfolge [Glas-Linse, Metall-Rahmen, Metall-Griff]. Die Linse ist
// transmissives PBR-Glas (vergroessert, was dahinter liegt); Rahmen/Griff sind
// opakes Chrom. Faithful aus Jans makeMagnifierMaterials.
export function makeMagnifierMaterials(params: TODOAnyP = {}): THREE.Material[] {
    const lens = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(1, 1, 1),
        metalness: 0,
        roughness: 0,
        transparent: false,
    });
    lens.transmission = 1;
    lens.ior = 1.5;
    // optische "Dicke" = Vergroesserung (Refraktions-Offset skaliert damit).
    lens.thickness = Math.max(100, (params.depth ?? 300) * 4);
    lens.clearcoat = 1;
    lens.clearcoatRoughness = 0;
    lens.userData.alphaNeeded = true;

    const finish: string = params.frameFinish ?? "metal";
    const frame = new THREE.MeshStandardMaterial({ transparent: false });
    if (finish === "black") {
        frame.color = new THREE.Color(0.05, 0.05, 0.06);
        frame.metalness = 0.3;
        frame.roughness = 0.5;
    } else if (finish === "white") {
        frame.color = new THREE.Color(0.92, 0.92, 0.94);
        frame.metalness = 0.1;
        frame.roughness = 0.4;
    } else {
        // Chrom
        frame.color = new THREE.Color(0.72, 0.73, 0.78);
        frame.metalness = 1;
        frame.roughness = 0.18;
    }
    frame.userData.alphaNeeded = false;
    const handle = frame.clone();
    handle.userData.alphaNeeded = false;
    return [lens, frame, handle];
}

// Gebackenes Material-Array fuer den pin: [Body (Farbe), Accent]. Accent ist
// weiss (map-Dot) oder Metall (needle/pole/spike). Faithful aus Jans
// makePinMaterials.
export function makePinMaterials(params: TODOAnyP = {}): THREE.Material[] {
    const style: string = params?.pinStyle ?? "map";
    const c = params?.pinColor ?? { r: 230, g: 60, b: 50, a: 1 };
    const body = new THREE.MeshStandardMaterial({ transparent: false });
    body.color = new THREE.Color((c.r ?? 230) / 255, (c.g ?? 60) / 255, (c.b ?? 50) / 255);
    body.metalness = 0.1;
    body.roughness = 0.45;
    body.userData.alphaNeeded = false;
    const accent = new THREE.MeshStandardMaterial({ transparent: false });
    if (style === "map") {
        accent.color = new THREE.Color(1, 1, 1);
        accent.metalness = 0;
        accent.roughness = 0.5;
    } else {
        accent.color = new THREE.Color(0.78, 0.79, 0.82);
        accent.metalness = 1;
        accent.roughness = 0.25;
    }
    accent.userData.alphaNeeded = false;
    return [body, accent];
}

/** Primitive mit gebackenen eigenen Materialien (nicht via overwriteMaterial):
 *  magnifier/pin (Multi-Material) + chess (Brett/Figuren/Labels mit je
 *  eigenen Materialien, effects/chess.ts). */
export const BAKED_PRIMITIVES: ReadonlySet<PrimitiveKind> = new Set<PrimitiveKind>([
    "magnifier",
    "pin",
    "chess",
]);

// Ein-Material-Primitive, die der Renderer verdrahtet (Geometrie via
// buildPrimitiveGeometry, ein Material). progressBar/progressRing bauen initial
// voll und werden pro Frame ueber den progress-Kanal umgebaut (renderLayer).
// NICHT enthalten: text3d/number3d (async Typeface, eigenes Modul), magnifier/
// pin/chess (eigene Materialien, s. BAKED_PRIMITIVES).
export const WIRED_PRIMITIVES: ReadonlySet<PrimitiveKind> = new Set<PrimitiveKind>([
    "box",
    "sphere",
    "cylinder",
    "cone",
    "pyramid",
    "torus",
    "tube",
    "plane",
    "roundedBox",
    "capsule",
    "icosahedron",
    "octahedron",
    "tetrahedron",
    "dodecahedron",
    "ring",
    "star",
    "torusKnot",
    "helix",
    "progressBar",
    "progressRing",
]);
